import { MessageCircle, ChevronDown, Truck, Clock } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { buildSms } from "@/lib/sms";
import { useLang } from "@/contexts/LangContext";
import { useSettings } from "@/contexts/SettingsContext";
import { LangToggle } from "@/components/LangToggle";

const LOGO =
  "https://customer-assets.emergentagent.com/job_nostalgic-euler-11/artifacts/tqf34lj8_2F5D3634-AB0D-41DC-B7DA-51C90C1F2482.png";

export const Hero = () => {
  const { t } = useLang();
  const { shop } = useSettings();
  const smsHref = buildSms({ body: "Hi, I'd like to place an order can you send me the list?" });

  return (
    <section data-testid={HOME.heroSection} className="relative isolate overflow-hidden grain">
      <div className="absolute inset-0 -z-10 drift">
        <div className="absolute -top-1/3 left-1/2 h-[700px] w-[700px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_center,rgba(0,255,65,0.20),rgba(0,255,65,0)_60%)]" />
        <div className="absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full bg-[radial-gradient(circle_at_center,rgba(0,255,65,0.10),rgba(0,255,65,0)_70%)]" />
      </div>

      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 pt-6 sm:px-8 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-16 w-16 sm:h-20 sm:w-20 shrink-0 overflow-hidden rounded-2xl bg-white ring-1 ring-white/10 shadow-[0_0_40px_rgba(0,255,65,0.25)]">
            <img src={LOGO} alt="One Stop Shop logo" className="h-full w-full object-contain" />
          </div>
          <div className="leading-none min-w-0">
            <p className="font-display text-xl sm:text-2xl font-extrabold tracking-tight">OSS</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00ff41] truncate">
              Ottawa · Gatineau
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <LangToggle />
          <a
            href={smsHref}
            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#00ff41]/40 bg-[#00ff41]/10 px-4 py-2 text-sm font-display font-semibold text-[#00ff41] hover:bg-[#00ff41]/20 transition"
          >
            <MessageCircle size={14} />
            {shop.phoneDisplay}
          </a>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-12 pb-10 sm:pt-20 sm:pb-16">
        <div className="flex items-center gap-2 mb-5 fade-up" style={{ animationDelay: "60ms" }}>
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00ff41] opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00ff41]" />
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-400">
            {t("open_now")}
          </span>
        </div>

        <h1
          className="font-display text-4xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight max-w-4xl fade-up"
          style={{ animationDelay: "120ms" }}
        >
          {t("h1_a")}
          <br />
          {t("h1_b")}
          <br />
          <span className="text-[#00ff41]">{t("h1_c")}</span>
        </h1>

        <p
          className="mt-6 max-w-xl text-base sm:text-lg text-neutral-300 leading-relaxed fade-up"
          style={{ animationDelay: "200ms" }}
        >
          {t("hero_body")}
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3 fade-up" style={{ animationDelay: "280ms" }}>
          <a
            data-testid={HOME.heroTextBtn}
            href={smsHref}
            className="neon-pulse inline-flex items-center gap-2 rounded-full bg-[#00ff41] px-6 py-3.5 font-display font-bold text-black hover:bg-[#00e63a] transition"
          >
            <MessageCircle size={18} />
            {t("text_to_order")}
          </a>
          <a
            data-testid={HOME.heroMenuBtn}
            href="#menu"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3.5 font-display font-semibold text-white hover:bg-white/10 transition"
          >
            {t("view_menu")}
            <ChevronDown size={16} />
          </a>
        </div>

        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl fade-up" style={{ animationDelay: "360ms" }}>
          <Fact icon={<Clock size={16} />} label="24/7" sub={t("open_every_night")} t={t} />
          <Fact icon={<Truck size={16} />} label={t("free_120")} sub={t("delivery_zone")} t={t} />
          <Fact icon={<MessageCircle size={16} />} label={t("text_us")} sub={shop.phoneDisplay} t={t} />
        </div>
      </div>
    </section>
  );
};

const Fact = ({ icon, label, sub, t }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-sm">
    <div className="flex items-center gap-2 text-[#00ff41]">
      {icon}
      <span className="font-mono text-[10px] uppercase tracking-[0.25em]">{t("live")}</span>
    </div>
    <p className="mt-2 font-display text-xl font-bold leading-none">{label}</p>
    <p className="mt-1 text-xs text-neutral-400">{sub}</p>
  </div>
);

export default Hero;
